import { Help, Search } from "@mui/icons-material";
import { Checkbox, Divider, FormControlLabel, FormLabel, Tooltip } from "@mui/material";
import { debounce, get, includes, toLower } from "lodash";
import React from "react";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import ErrorIcon from "../../../common/js/components/ErrorIcon";
import { sprinklerTypes } from "../listing/constants";
import { specialtyTypeFilterOptions } from "../property/constants";
import searchConstants from "../search/constants";
import { propertyTypeOptions } from "../types/propertyTypes";
import { yes_no_options } from "../utils/constants";
import {
    cap_rate_basis_options,
    interest_type_options,
    investor_type_options,
    lease_renewal_options,
    lease_representation_options,
    sale_representation_options,
} from "./constants";
import "./DealForm.css";

const debounceSearchCompany = debounce((handleSearchCompany, input) => {
    handleSearchCompany(input, "BUSINESS", true);
}, searchConstants["DEBOUNCE_SEARCH_DELAY"]);

const DealForm = (props) => {
    const {
        autocompleteResults,
        deal,
        disableInputs,
        errors,
        handleBlurValidate,
        handleCheckboxClick,
        handleDealChange,
        handleInput,
        handleSearchCompany,
        isAppraisalComp,
        isOutsideComp,
        transactionType,
        userProfile,
        handleRepresentationTypeChange,
    } = props;

    const getCompanyFieldLabel = (field, companyType) => {
        let labelText = "";
        let buyerTenantText = companyType === "SELLER_LANDLORD" ? "Landlord" : "Tenant";
        if (transactionType === "Sale") {
            buyerTenantText = companyType === "SELLER_LANDLORD" ? "Seller" : "Buyer";
        }
        if (isOutsideOrAppraisal()) {
            switch (field) {
                case "legalName":
                    labelText = buyerTenantText + " Name (Legal)";
                    break;
                case "displayName":
                    labelText = buyerTenantText + " Name (Actual)";
                    break;
                case "parentCompany":
                    labelText = buyerTenantText + " Name (Parent Company/Institutional Owner)";
                    break;
                default:
                    break;
            }
        } else {
            switch (field) {
                case "legalName":
                    labelText = buyerTenantText + " Name (Legal)*";
                    break;
                case "displayName":
                    labelText = buyerTenantText + " Name (Actual)*";
                    break;
                case "parentCompany":
                    labelText = buyerTenantText + " Name (Parent Company/Institutional Owner)";
                    break;
                default:
                    break;
            }
        }
        return labelText;
    };

    const getCompanyPlaceholderText = (field, companyType) => {
        let placeholderText = "";
        let buyerTenantText = companyType === "SELLER_LANDLORD" ? "landlord" : "tenant";
        if (transactionType === "Sale") {
            buyerTenantText = companyType === "SELLER_LANDLORD" ? "building owner" : "buyer";
        }
        switch (field) {
            case "legalName":
                placeholderText = "Enter " + buyerTenantText + " name (legal)";
                break;
            case "displayName":
                placeholderText = "Enter " + buyerTenantText + " name (common)";
                break;
            case "parentCompany":
                placeholderText = "Enter " + buyerTenantText + " name (parent company/institutional owner)";
                break;
            default:
                break;
        }
        return placeholderText;
    };

    const isOutsideOrAppraisal = () => {
        return includes(["Outside", "Outside Draft", "Appraisal", "Appraisal Draft"], deal.compStatus);
    };

    const handleAppraisalOrOutsideCompCompany = (company, name, value, parentFlg = false) => {
        if (parentFlg) handleInput(company + ".parentFlg", true);
        handleInput(company + "." + name, value);
        if (get(deal, company + ".companyTypes", null) == null) {
            if (isOutsideComp) {
                handleInput(company + ".companyTypes", ["OUTSIDE_COMP"]);
                handleInput(company + ".approvedFlg", true);
            } else if (isAppraisalComp) {
                handleInput(company + ".companyTypes", ["APPRAISAL"]);
                handleInput(company + ".approvedFlg", true);
            }
        }
    };

    const buildCompanyTooltipString = (company) => {
        const companyKey = company?.companyKey ?? "";
        const legalName = company?.legalName ?? "";
        const dbaName = company?.displayName ?? "";
        return `${companyKey} | ${legalName} | ${dbaName}`;
    };

    const representation_options =
        transactionType === "Sale" ? sale_representation_options : lease_representation_options;

    return (
        <div>
            <div className="dealFormFlexContainerUnresponsive">
                <div className="flexGrowSingle">
                    <BSInput
                        id="dealName"
                        label={isOutsideOrAppraisal() ? "COMP NAME*" : "DEAL NAME*"}
                        toolTipText="This is the name under which the deal can be searched (e.g. Tenant Name, Landlord Name, Property Namess, etc.)"
                        helperText="Required"
                        errorText="Required"
                        error={get(errors, "listing.listingName")}
                        value={get(deal, "listing.listingName")}
                        onChange={(e) => handleInput("listing.listingName", e.target.value)}
                        placeholder={isOutsideOrAppraisal() ? "Enter a comp name" : "Enter a deal name"}
                        disabled={disableInputs}
                        onBlurValidate={(id, value) =>
                            handleBlurValidate(transactionType, "listing.listingName", value)
                        }
                    />
                </div>
                <div className="flexGrowSingle">
                    {deal.propertyType !== "Religious" && (
                        <BSSelect
                            id="specialtyType"
                            label="Sub-Type*"
                            helperText="Required"
                            errorText="Required"
                            error={get(errors, "listing.attributes.specialtyType")}
                            value={get(deal, "listing.attributes.specialtyType")}
                            onChange={(e) => handleInput("listing.attributes.specialtyType", e.target.value)}
                            options={specialtyTypeFilterOptions(get(deal, "listing.attributes.useType"))}
                            disabled={disableInputs}
                            onBlurValidate={(id, value) =>
                                handleBlurValidate(deal?.propertyType, "listing.attributes.specialtyType", value)
                            }
                        />
                    )}
                </div>
            </div>
            <div className="dealFormFlexContainer">
                <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                    <div className="flexGrowSingle">
                        <BSSelect
                            id="propertyType"
                            label="Property Type"
                            value={deal.propertyType}
                            error={errors.propertyType}
                            options={propertyTypeOptions}
                            disabled={true}
                        />
                    </div>
                    <div className="flexGrowSingle">
                        <BSInput
                            id="suite"
                            label="Suite"
                            value={get(deal, "listing.attributes.suite")}
                            onChange={(e) => handleInput("listing.attributes.suite", e.target.value)}
                            placeholder="Enter suite"
                            disabled={disableInputs}
                        />
                    </div>
                </div>
                <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                    <div className="flexGrowSingle">
                        <BSInput
                            id="transactionDate"
                            label="Date Signed*"
                            value={deal.transactionDate}
                            error={errors.transactionDate}
                            onChange={(e) => handleInput(e.target.id, e.target.value)}
                            helperText="Required"
                            errorText="Required (mm/dd/yyyy)"
                            placeholder="Date Signed"
                            disabled={disableInputs}
                            type="date"
                            onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                        />
                    </div>
                    {toLower(transactionType) === "sale" && (
                        <div className="flexGrowSingle">
                            <BSInput
                                id="closedDate"
                                label="Closed Date*"
                                value={deal.closedDate}
                                error={errors.closedDate}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                helperText="Required"
                                errorText="Required (mm/dd/yyyy)"
                                placeholder="Closed Date"
                                disabled={disableInputs}
                                type="date"
                                onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/*outside or appraisal section*/}
            {isOutsideOrAppraisal() && (
                <React.Fragment>
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="legalName"
                                    label={getCompanyFieldLabel("legalName", "BUYER_TENANT")}
                                    value={
                                        get(deal, "buyerTenantCompany", null)
                                            ? get(deal, "buyerTenantCompany.legalName", "")
                                            : null
                                    }
                                    onChange={(event) =>
                                        handleAppraisalOrOutsideCompCompany(
                                            "buyerTenantCompany",
                                            "legalName",
                                            event.target.value
                                        )
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("legalName", "BUYER_TENANT")}
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="displayName"
                                    label={getCompanyFieldLabel("displayName", "BUYER_TENANT")}
                                    value={get(deal, "buyerTenantCompany.displayName", "")}
                                    onChange={(event) =>
                                        handleAppraisalOrOutsideCompCompany(
                                            "buyerTenantCompany",
                                            "displayName",
                                            event.target.value
                                        )
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("displayName", "BUYER_TENANT")}
                                />
                            </div>
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="parentCompany"
                                label={getCompanyFieldLabel("parentCompany", "BUYER_TENANT")}
                                value={get(deal, "buyerTenantCompany.parentCompany.displayName", "")}
                                onChange={(event) =>
                                    handleAppraisalOrOutsideCompCompany(
                                        "buyerTenantCompany.parentCompany",
                                        "displayName",
                                        event.target.value,
                                        true
                                    )
                                }
                                disabled={disableInputs}
                                placeholder={getCompanyPlaceholderText("parentCompany", "BUYER_TENANT")}
                            />
                        </div>
                    </div>

                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="legalName"
                                    label={getCompanyFieldLabel("legalName", "SELLER_LANDLORD")}
                                    value={
                                        get(deal, "sellerLandlordCompany", null)
                                            ? get(deal, "sellerLandlordCompany.legalName", "")
                                            : null
                                    }
                                    onChange={(event) =>
                                        handleAppraisalOrOutsideCompCompany(
                                            "sellerLandlordCompany",
                                            "legalName",
                                            event.target.value
                                        )
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("legalName", "SELLER_LANDLORD")}
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="displayName"
                                    label={getCompanyFieldLabel("displayName", "SELLER_LANDLORD")}
                                    value={get(deal, "sellerLandlordCompany.displayName", "")}
                                    onChange={(event) =>
                                        handleAppraisalOrOutsideCompCompany(
                                            "sellerLandlordCompany",
                                            "displayName",
                                            event.target.value
                                        )
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("displayName", "SELLER_LANDLORD")}
                                />
                            </div>
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="parentCompany"
                                label={getCompanyFieldLabel("parentCompany", "SELLER_LANDLORD")}
                                value={get(deal, "sellerLandlordCompany.parentCompany.displayName", "")}
                                onChange={(event) =>
                                    handleAppraisalOrOutsideCompCompany(
                                        "sellerLandlordCompany.parentCompany",
                                        "displayName",
                                        event.target.value,
                                        true
                                    )
                                }
                                disabled={disableInputs}
                                placeholder={getCompanyPlaceholderText("parentCompany", "SELLER_LANDLORD")}
                            />
                        </div>
                    </div>
                </React.Fragment>
            )}

            {!isOutsideOrAppraisal() && (
                <React.Fragment>
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="buyerTenantLegalCompanyName"
                                    label={getCompanyFieldLabel("legalName", "BUYER_TENANT")}
                                    error={get(errors, "buyerTenantLegalCompanyName", null)}
                                    errorText="Required"
                                    helperText={isOutsideOrAppraisal() ? "" : "Required"}
                                    value={get(deal, "buyerTenantLegalCompanyName", null)}
                                    onChange={(event) => handleInput("buyerTenantLegalCompanyName", event.target.value)}
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("legalName", "BUYER_TENANT")}
                                    onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="buyerTenantCommonCompanyName"
                                    label={getCompanyFieldLabel("displayName", "BUYER_TENANT")}
                                    error={get(errors, "buyerTenantCommonCompanyName", null)}
                                    errorText="Required"
                                    helperText={isOutsideOrAppraisal() ? "" : "Required"}
                                    value={get(deal, "buyerTenantCommonCompanyName", "")}
                                    onChange={(event) =>
                                        handleInput("buyerTenantCommonCompanyName", event.target.value)
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("displayName", "BUYER_TENANT")}
                                    onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                                />
                            </div>
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="buyerTenantInstitutionalOwner"
                                label={getCompanyFieldLabel("parentCompany", "BUYER_TENANT")}
                                error={get(errors, "buyerTenantInstitutionalOwner", null)}
                                value={get(deal, "buyerTenantInstitutionalOwner", "")}
                                onChange={(event) => handleInput("buyerTenantInstitutionalOwner", event.target.value)}
                                disabled={disableInputs}
                                placeholder={getCompanyPlaceholderText("parentCompany", "BUYER_TENANT")}
                            />
                        </div>
                    </div>
                    {/*if role is admin or CDT have autocomplete company inputs */}
                    {includes(["dataAdmin", "dataEntry"], get(userProfile, "role")) && (
                        <div style={{ marginBottom: "20px" }}>
                            <span className="dealFormDefTerm">
                                {" "}
                                BUYER COMPANY
                                <React.Fragment>
                                    <Tooltip
                                        id="buyerCompanyInfo"
                                        placement="top"
                                        className="statBoxTooltip"
                                        title={buildCompanyTooltipString(deal.buyerTenantCompany)}
                                    >
                                        <span data-tooltip-id="buyerCompanyInfo">
                                            <Help
                                                style={{
                                                    fontSize: "110%",
                                                    padding: "0 0 3px 3px",
                                                    color: "grey",
                                                }}
                                            />
                                            {errors.buyerTenantCompany && <ErrorIcon />}
                                        </span>
                                    </Tooltip>
                                </React.Fragment>
                            </span>
                            <Autocomplete
                                id="buyerCompany"
                                hideErrorIcon
                                error={get(errors, `buyerTenantCompany`, "")}
                                initialInputValue={`${get(deal, `buyerTenantCompany.displayName`, "")}`}
                                itemToString={(i) => get(i, `legalName`, null) + " - " + get(i, `companyKey`, null)} //can add the other fields as well
                                onSelect={(company) => handleInput("buyerTenantCompany", company)}
                                placeholder="Enter Approved Company"
                                searchFunction={(input) => debounceSearchCompany(handleSearchCompany, input)}
                                startAdornment={<Search />}
                                suggestions={autocompleteResults}
                                disabled={disableInputs}
                                errorText="Required"
                                extraFormControlStyles={{ marginBottom: 0 }}
                                helperText={<span style={{ color: "#E53935" }}>Required</span>}
                                options={{
                                    key: "companyKey",
                                    display: (suggestion) => {
                                        return (
                                            get(suggestion, "companyKey", "") +
                                            " / " +
                                            get(suggestion, "legalName", "") +
                                            " / " +
                                            get(suggestion, "displayName", "")
                                        );
                                    },
                                }}
                                onBlurValidate={(id, value) =>
                                    handleBlurValidate("companyAdminConstraints", "buyerTenantCompany", value)
                                }
                            />
                            <br />

                            <Divider />
                        </div>
                    )}
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="sellerLandlordLegalCompanyName"
                                    label={getCompanyFieldLabel("legalName", "SELLER_LANDLORD")}
                                    error={get(errors, "sellerLandlordLegalCompanyName", null)}
                                    errorText="Required"
                                    helperText={isOutsideOrAppraisal() ? "" : "Required"}
                                    value={get(deal, "sellerLandlordLegalCompanyName", null)}
                                    onChange={(event) =>
                                        handleInput("sellerLandlordLegalCompanyName", event.target.value)
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("legalName", "SELLER_LANDLORD")}
                                    onBlurValidate={(id, value) =>
                                        handleBlurValidate("sellerLandlordCompany", id, value)
                                    }
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="sellerLandlordCommonCompanyName"
                                    label={getCompanyFieldLabel("displayName", "SELLER_LANDLORD")}
                                    error={get(errors, "sellerLandlordCommonCompanyName", null)}
                                    errorText="Required"
                                    helperText={isOutsideOrAppraisal() ? "" : "Required"}
                                    value={get(deal, "sellerLandlordCommonCompanyName", "")}
                                    onChange={(event) =>
                                        handleInput("sellerLandlordCommonCompanyName", event.target.value)
                                    }
                                    disabled={disableInputs}
                                    placeholder={getCompanyPlaceholderText("displayName", "SELLER_LANDLORD")}
                                    onBlurValidate={(id, value) =>
                                        handleBlurValidate("sellerLandlordCompany", id, value)
                                    }
                                />
                            </div>
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="sellerLandlordInstitutionalOwner"
                                label={getCompanyFieldLabel("parentCompany", "SELLER_LANDLORD")}
                                error={get(errors, "sellerLandlordInstitutionalOwner", null)}
                                value={get(deal, "sellerLandlordInstitutionalOwner", "")}
                                onChange={(event) =>
                                    handleInput("sellerLandlordInstitutionalOwner", event.target.value)
                                }
                                disabled={disableInputs}
                                placeholder={getCompanyPlaceholderText("parentCompany", "SELLER_LANDLORD")}
                            />
                        </div>
                    </div>

                    <div>
                        {/*if role is admin or CDT have autocomplete company inputs */}
                        {includes(["dataAdmin", "dataEntry"], get(userProfile, "role", null)) && (
                            <div style={{ marginBottom: "20px" }}>
                                <span className="dealFormDefTerm">
                                    SELLER COMPANY
                                    <React.Fragment>
                                        <Tooltip
                                            id="sellerCompanyInfo"
                                            placement="top"
                                            className="statBoxTooltip"
                                            title={buildCompanyTooltipString(deal.sellerLandlordCompany)}
                                        >
                                            <span data-tooltip-id="sellerCompanyInfo">
                                                <Help
                                                    style={{
                                                        fontSize: "110%",
                                                        padding: "0 0 3px 3px",
                                                        color: "grey",
                                                    }}
                                                />
                                                {errors.sellerLandlordCompany && <ErrorIcon />}
                                            </span>
                                        </Tooltip>
                                    </React.Fragment>
                                </span>
                                <Autocomplete
                                    id="sellerCompany"
                                    hideErrorIcon
                                    extraFormControlStyles={{ marginBottom: 0 }}
                                    error={get(errors, `sellerLandlordCompany`, "")}
                                    initialInputValue={`${get(deal, `sellerLandlordCompany.displayName`, "")}`}
                                    itemToString={(i) => get(i, `legalName`, null)} //can add the other fields as well
                                    onSelect={(company) => handleInput("sellerLandlordCompany", company)}
                                    placeholder="Enter Approved Company"
                                    searchFunction={(input) => debounceSearchCompany(handleSearchCompany, input)}
                                    startAdornment={<Search />}
                                    suggestions={autocompleteResults}
                                    disabled={disableInputs}
                                    errorText="Required"
                                    helperText={<span style={{ color: "#E53935" }}>Required</span>}
                                    options={{
                                        key: "companyKey",
                                        display: (suggestion) => {
                                            return (
                                                get(suggestion, "companyKey", "") +
                                                " / " +
                                                get(suggestion, "legalName", "") +
                                                " / " +
                                                get(suggestion, "displayName", "")
                                            );
                                        },
                                    }}
                                    onBlurValidate={(id, value) =>
                                        handleBlurValidate("companyAdminConstraints", "sellerLandlordCompany", value)
                                    }
                                />
                                <br />
                                <Divider />
                            </div>
                        )}
                    </div>
                </React.Fragment>
            )}

            {toLower(transactionType) === "sale" && !isAppraisalComp && (
                <div className="dealFormFlexContainerUnresponsive">
                    <div className="flexGrowSingle">
                        <BSInput
                            id="totalConsiderationAmount"
                            label="Sale Price*"
                            helperText="Required (ex. 981.01)"
                            errorText="Required (ex. 981.01)"
                            error={errors.totalConsiderationAmount}
                            value={deal.totalConsiderationAmount}
                            onChange={handleDealChange}
                            placeholder="Enter sale price"
                            disabled={disableInputs}
                            startAdornment="$"
                            onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                            numeric
                        />
                    </div>
                    <div className="flexGrowSingle">
                        <BSInput
                            id="squareFeet"
                            label="Purchase SF*"
                            error={errors.squareFeet}
                            value={deal.squareFeet}
                            onChange={handleDealChange}
                            placeholder="Enter total transaction SF"
                            disabled={disableInputs}
                            helperText="Required (ex. 15678)"
                            errorText="Required (ex. 15678)"
                            onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                        />
                    </div>
                </div>
            )}

            {isAppraisalComp && toLower(transactionType) === "lease" && (
                <React.Fragment>
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="transactionLandSf"
                                    label="Leased Land SF"
                                    error={errors.transactionLandSf}
                                    value={deal.transactionLandSf}
                                    onChange={handleDealChange}
                                    placeholder="Enter leased land SF"
                                    disabled={disableInputs}
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="landToBldgRatio"
                                    label="Land To Bldg Ratio"
                                    error={errors.landToBldgRatio}
                                    value={deal.landToBldgRatio}
                                    disabled={true}
                                />
                            </div>
                        </div>
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="monthsOnMarket"
                                    label="Months on Market"
                                    error={errors.monthsOnMarket}
                                    value={deal.monthsOnMarket}
                                    onChange={handleDealChange}
                                    placeholder="Enter months on market"
                                    disabled={disableInputs}
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="occupancyRate"
                                    label="Occupancy %"
                                    error={errors.occupancyRate}
                                    value={deal.occupancyRate}
                                    onChange={handleDealChange}
                                    placeholder="Enter occupancy %"
                                    disabled={disableInputs}
                                    numeric
                                />
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            )}

            {isAppraisalComp && toLower(transactionType) === "sale" && (
                <React.Fragment>
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="totalConsiderationAmount"
                                    label="Sale Price*"
                                    helperText="Required (ex. 981.01)"
                                    errorText="Required (ex. 981.01)"
                                    error={errors.totalConsiderationAmount}
                                    value={deal.totalConsiderationAmount}
                                    onChange={handleDealChange}
                                    placeholder="Enter sale price"
                                    disabled={disableInputs}
                                    startAdornment="$"
                                    numeric
                                    onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                                />
                            </div>

                            {includes(["Multifamily", "Hospitality"], get(deal, "propertyType", null)) && (
                                <div className="flexGrowSingle">
                                    <BSInput
                                        id="salePricePerUnit"
                                        label={
                                            get(deal, "propertyType", null) === "Hospitality"
                                                ? "Sale Price per Room"
                                                : "Sale Price Per Unit"
                                        }
                                        placeholder={
                                            get(deal, "propertyType", null) === "Hospitality"
                                                ? "Sale price per room"
                                                : "Sale price per unit"
                                        }
                                        error={errors.salePricePerUnit}
                                        value={deal.salePricePerUnit}
                                        disabled={true}
                                        extraInputStyles={{ whiteSpace: "nowrap" }}
                                        endAdornment={
                                            get(deal, "propertyType", null) === "Hospitality" ? "$/room" : "$/unit"
                                        }
                                        numeric
                                    />
                                </div>
                            )}
                        </div>
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="saleAdjustment"
                                    label="Sale Adjustment"
                                    error={errors.saleAdjustment}
                                    value={deal.saleAdjustment}
                                    onChange={handleDealChange}
                                    placeholder="Enter sale adjustment"
                                    disabled={disableInputs}
                                    startAdornment="$"
                                    numeric
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="analysisPrice"
                                    label="Analysis Price"
                                    error={errors.analysisPrice}
                                    value={deal.analysisPrice}
                                    onChange={handleDealChange}
                                    disabled={true}
                                    startAdornment="$"
                                    numeric
                                />
                            </div>
                        </div>
                    </div>

                    <div className="dealFormFlexContainerUnresponsive">
                        <div className="flexGrowSingle">
                            <BSInput
                                id="squareFeet"
                                label="Purchase SF*"
                                error={errors.squareFeet}
                                value={deal.squareFeet}
                                onChange={handleDealChange}
                                placeholder="Enter purchased building SF"
                                disabled={disableInputs}
                                helperText="Required (ex. 15678)"
                                errorText="Required (ex. 15678)"
                                onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="bldgSalePricePerSf"
                                label="Purchased Building $/sf"
                                error={errors.bldgSalePricePerSf}
                                value={deal.bldgSalePricePerSf}
                                onChange={handleDealChange}
                                disabled={true}
                                extraInputStyles={{ whiteSpace: "nowrap" }}
                                endAdornment="$/sf"
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive">
                        <div className="flexGrowSingle">
                            <BSInput
                                id="transactionLandSf"
                                label="Purchased Land SF"
                                error={errors.transactionLandSf}
                                value={deal.transactionLandSf}
                                onChange={handleDealChange}
                                placeholder="Enter purchased land SF"
                                disabled={disableInputs}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="landSalePricePerSf"
                                label="Purchased Land $/sf"
                                error={errors.landSalePricePerSf}
                                value={deal.landSalePricePerSf}
                                onChange={handleDealChange}
                                disabled={true}
                                extraInputStyles={{ whiteSpace: "nowrap" }}
                                endAdornment="$/sf"
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive">
                        <div className="flexGrowSingle">
                            <BSInput
                                id="landToBldgRatio"
                                label="Land To Bldg Ratio"
                                error={errors.landToBldgRatio}
                                value={deal.landToBldgRatio}
                                onChange={handleDealChange}
                                disabled={true}
                                numeric
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="exciseTaxNumber"
                                label="Excise Tax Number"
                                error={errors.exciseTaxNumber}
                                value={deal.exciseTaxNumber}
                                onChange={handleDealChange}
                                placeholder="Enter excise tax number"
                                disabled={disableInputs}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="monthsOnMarket"
                                label="Months on Market"
                                error={errors.monthsOnMarket}
                                value={deal.monthsOnMarket}
                                onChange={handleDealChange}
                                placeholder="Enter months on market"
                                disabled={disableInputs}
                                integer
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive">
                        <div className="flexGrowSingle">
                            <BSSelect
                                id="investorType"
                                label="Investor Type"
                                error={errors.investorType}
                                value={deal.investorType}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                placeholder="Select investor type"
                                options={investor_type_options}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSSelect
                                id="interestType"
                                label="Interest"
                                error={errors.interestType}
                                value={deal.interestType}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                placeholder="Select interest type"
                                options={interest_type_options}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSSelect
                                id="capRateBasis"
                                label="Cap Rate Basis"
                                error={errors.capRateBasis}
                                value={deal.capRateBasis}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                placeholder="Select cap rate basis"
                                options={cap_rate_basis_options}
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive">
                        <div className="flexGrowSingle">
                            <BSInput
                                id="noi"
                                label="NOI"
                                error={errors.noi}
                                value={deal.noi}
                                onChange={handleDealChange}
                                placeholder="Enter NOI"
                                startAdornment="$"
                                disabled={disableInputs}
                                numeric
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="noiPerSf"
                                label="NOI Per SF"
                                error={errors.noiPerSf}
                                value={deal.noiPerSf}
                                disabled={true}
                                extraInputStyles={{ whiteSpace: "nowrap" }}
                                endAdornment="$/sf"
                                numeric
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <BSInput
                                id="capRate"
                                label="Cap Rate"
                                value={get(deal, "listing.capRate")}
                                error={get(errors, "listing.capRate")}
                                onChange={(e) => handleInput("listing.capRate", e.target.value)}
                                errorText="Must be a number (ex. 5.7)"
                                placeholder="Enter cap rate % (optional)"
                                disabled={disableInputs}
                                endAdornment="%"
                                numeric
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainer">
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="discountRatePerIrr"
                                    label="Discount Rate/IRR"
                                    error={errors.discountRatePerIrr}
                                    value={deal.discountRatePerIrr}
                                    onChange={handleDealChange}
                                    placeholder="Enter discount rate/IRR"
                                    disabled={disableInputs}
                                    numeric
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="reversionCapRate"
                                    label="Reversion Cap Rate"
                                    error={errors.reversionCapRate}
                                    value={deal.reversionCapRate}
                                    onChange={handleDealChange}
                                    placeholder="Enter reversion cap rate"
                                    disabled={disableInputs}
                                    numeric
                                />
                            </div>
                        </div>
                        <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="numTenants"
                                    label="Tenants"
                                    error={errors.numTenants}
                                    value={deal.numTenants}
                                    onChange={handleDealChange}
                                    placeholder="Enter tenants"
                                    disabled={disableInputs}
                                    integer
                                />
                            </div>
                            <div className="flexGrowSingle">
                                <BSInput
                                    id="occupancyRate"
                                    label="Occupancy %"
                                    error={errors.occupancyRate}
                                    value={deal.occupancyRate}
                                    onChange={handleDealChange}
                                    placeholder="Enter occupancy"
                                    disabled={disableInputs}
                                    numeric
                                />
                            </div>
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive flexGrowSingle dealFormAlignItemsCenter">
                        <div className="flexGrowSingle">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={deal.sellerFinancedFlg ? true : false}
                                        onChange={(e) => handleInput("sellerFinancedFlg", e.target.checked)}
                                        value="sellerFinancedFlg"
                                        disabled={false}
                                        color="primary"
                                    />
                                }
                                label="Seller Financed"
                            />
                        </div>
                        <div className="dealFormAppraisalTerms">
                            <BSInput
                                id="sellerLoanTerms"
                                label="Seller Loan Terms"
                                error={errors.sellerLoanTerms}
                                value={deal.sellerLoanTerms}
                                onChange={handleDealChange}
                                placeholder="Enter seller loan terms"
                                disabled={disableInputs}
                            />
                        </div>
                    </div>
                    <div className="dealFormFlexContainerUnresponsive flexGrowSingle dealFormAlignItemsCenter">
                        <div className="flexGrowSingle">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={deal.loanAssumedFlg ? true : false}
                                        onChange={(e) => handleInput("loanAssumedFlg", e.target.checked)}
                                        value="loanAssumedFlg"
                                        disabled={false}
                                        color="primary"
                                    />
                                }
                                label="Loan Assumed"
                            />
                        </div>
                        <div className="dealFormAppraisalTerms">
                            <BSInput
                                id="loanAssumedTerms"
                                label="Loan Assumed Terms"
                                error={errors.loanAssumedTerms}
                                value={deal.loanAssumedTerms}
                                onChange={handleDealChange}
                                placeholder="Enter loan assumed terms"
                                disabled={disableInputs}
                            />
                        </div>
                    </div>
                </React.Fragment>
            )}

            <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                <div className="dealFormFlexContainerUnresponsive flexGrowSingle">
                    <div className=" flexGrowSingle">
                        <BSSelect
                            id="representationType"
                            label={isOutsideOrAppraisal() ? "Representation Type" : "Representation Type*"}
                            helperText={!isOutsideOrAppraisal() && "Required"}
                            errorText={!isOutsideOrAppraisal() && "Required"}
                            error={errors.representationType}
                            value={deal.representationType}
                            onChange={(e) => handleRepresentationTypeChange(e.target.id, e.target.value, deal.dealKey)}
                            disabled={disableInputs}
                            placeholder="Select representation"
                            options={representation_options}
                            onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                        />
                    </div>

                    {includes(["Sale"], deal.transactionType) && (
                        <div className="flexGrowSingle">
                            <BSSelect
                                id="leaseBackFlg"
                                label={isOutsideOrAppraisal() ? "Lease Back" : "Lease Back*"}
                                options={yes_no_options}
                                helperText={!isOutsideOrAppraisal() && "Required"}
                                errorText={!isOutsideOrAppraisal() && "Required"}
                                error={errors.leaseBackFlg}
                                value={deal.leaseBackFlg}
                                onChange={(e) => {
                                    handleInput(e.target.id, e.target.value);
                                    if (e.target.value === false || e.target.value === "false") {
                                        handleInput("spaceType", null);
                                    }
                                }}
                                disabled={disableInputs}
                                onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                            />
                        </div>
                    )}
                    {includes(["Lease"], deal.transactionType) && (
                        <div className="flexGrowSingle">
                            <BSSelect
                                id="renewalFlg"
                                label="New Lease/Renewal"
                                value={deal.renewalFlg}
                                error={errors.renewalFlg}
                                errorText="Required"
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                helperText="Required"
                                placeholder="Select"
                                options={lease_renewal_options}
                                disabled={disableInputs}
                                onBlurValidate={(id, value) => handleBlurValidate(transactionType, id, value)}
                            />
                        </div>
                    )}
                </div>
                {(includes(["Lease"], deal.transactionType) ||
                    (includes(["Sale"], deal.transactionType) &&
                        (deal.leaseBackFlg === "true" || deal.leaseBackFlg === true))) && (
                    <div className="flexGrowSingle">
                        <BSSelect
                            id="tenantSpace"
                            label="Tenant Space"
                            value={deal.spaceType}
                            error={errors.spaceType}
                            errorText={isOutsideComp || isAppraisalComp ? "" : "Required"}
                            onChange={(e) => handleInput("spaceType", e.target.value)}
                            helperText={isOutsideComp || isAppraisalComp ? "" : "Required"}
                            placeholder="Select"
                            options={[
                                {
                                    value: "",
                                    label: "Select tenant space type ...",
                                    disabled: true,
                                },
                                {
                                    value: "EXPANSION",
                                    label: "Expansion",
                                },
                                {
                                    value: "CONTRACTION",
                                    label: "Contraction",
                                },
                                {
                                    value: "EXISTING_EQUIVALENT",
                                    label: "Existing/Equivalent",
                                },
                            ]}
                            disabled={disableInputs}
                            onBlurValidate={(id, value) => handleBlurValidate(transactionType, "spaceType", value)}
                        />
                    </div>
                )}
            </div>
            <div>
                <FormLabel component="legend" style={{ paddingTop: "25px", marginBottom: "5px" }}>
                    Additional Information
                </FormLabel>
            </div>
            <div className="dealFormCheckboxContainer">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={get(deal, "listing.subleaseFlg", false) ? true : false}
                            onChange={(e) => handleInput("listing.subleaseFlg", e.target.checked)}
                            value="subleaseFlg"
                            disabled={disableInputs}
                            color="primary"
                        />
                    }
                    label="Is this a Sublease?"
                />
                {isAppraisalComp && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deal.offMarketTransactionFlg ? true : false}
                                onChange={(e) => handleCheckboxClick("offMarketTransactionFlg", e)}
                                value="offMarketTransactionFlg"
                                disabled={disableInputs}
                                color="primary"
                            />
                        }
                        label="Off Market Transaction?"
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={deal.kmManagedFlg ? true : false}
                            onChange={(e) => handleCheckboxClick("kmManagedFlg", e)}
                            value="kmManagedFlg"
                            disabled={disableInputs}
                            color="primary"
                        />
                    }
                    label="Is this a KM managed property?"
                />

                {!isAppraisalComp && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deal.includeOnBrokerBioFlg ? true : false}
                                onChange={(e) => handleCheckboxClick("includeOnBrokerBioFlg", e)}
                                value="includeOnBrokerBioFlg"
                                disabled={disableInputs}
                                color="primary"
                            />
                        }
                        label="Include on Broker Bio page?"
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={deal.kmExclusiveFlg}
                            onChange={(e) => handleCheckboxClick("kmExclusiveFlg", e)}
                            value="kmExclusiveFlg"
                            disabled={disableInputs}
                            color="primary"
                        />
                    }
                    label="Is this a KM Exclusive?"
                />
                {isAppraisalComp && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deal.includeOnBrokerBioFlg ? true : false}
                                onChange={(e) => handleCheckboxClick("includeOnBrokerBioFlg", e)}
                                value="includeOnBrokerBioFlg"
                                disabled={disableInputs}
                                color="primary"
                            />
                        }
                        label="Include on Broker Bio page?"
                    />
                )}
            </div>
            <div className="dealFormSprinklers">
                <BSSelect
                    id="sprinklers"
                    label="Sprinklers"
                    error={get(errors, "listing.attributes.sprinklers")}
                    value={get(deal, "listing.attributes.sprinklers")}
                    onChange={(e) => handleInput("listing.attributes.sprinklers", e.target.value)}
                    options={sprinklerTypes}
                    disabled={disableInputs}
                />
            </div>

            {isOutsideOrAppraisal() && (
                <React.Fragment>
                    <Divider />
                    <div className="dealFormFlexContainerUnresponsive dealFormAlignItemsCenter">
                        <div className="flexGrowSingle">
                            <span style={{ fontSize: "14px" }}>{`Created By: `}</span>
                            <span style={{ fontSize: "14px" }}>{deal.auditCreatedBy}</span>
                        </div>
                        <div className="flexGrowSingle">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={deal.approvedBy ? true : false}
                                        onChange={() =>
                                            handleInput("approvedBy", deal.approvedBy ? null : userProfile.user)
                                        }
                                        value="approvedBy"
                                        disabled={false}
                                        color="primary"
                                    />
                                }
                                label={deal.approvedBy ? `Validated By: ${deal.approvedBy}` : "Validate"}
                            />
                        </div>
                        <div className="flexGrowSingle">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={deal.isPartiallyComplete ? true : false}
                                        onChange={(e) => handleInput("isPartiallyComplete", e.target.checked)}
                                        disabled={false}
                                        color="primary"
                                    />
                                }
                                label={"Partial Comp"}
                            />
                        </div>
                    </div>
                </React.Fragment>
            )}
        </div>
    );
};

export default DealForm;
